// Two flows on one page based on URL ?token=...:
//   - no token: request-a-reset form
//   - with token: set-new-password form
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset, requestPasswordReset } from "../api/auth";
import { ApiError } from "../api/client";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");

  return (
    <>
      <header className="flex items-center justify-between border-b border-rule-soft px-7 py-5">
        <Link to="/" className="font-display text-2xl tracking-[-0.04em]">
          Varo
        </Link>
        <Link
          to="/login"
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-mute hover:text-terra"
        >
          Back to sign in
        </Link>
      </header>

      <main className="mx-auto max-w-3xl px-7 py-12">
        <p className="eyebrow eyebrow-accent">04. Reset</p>
        <h1 className="mt-3 max-w-[22ch] text-5xl tracking-tight">
          {token ? "Set a new password." : "Reset your password."}
        </h1>

        <div className="mt-9">{token ? <ConfirmReset token={token} /> : <RequestReset />}</div>
      </main>
    </>
  );
}

function RequestReset() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setDone(true);
    } catch {
      // We always show success regardless — server doesn't leak existence.
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-lg border border-rule bg-cream-card p-8 text-center">
        <p className="eyebrow eyebrow-accent">Step A · Done</p>
        <h2 className="mt-4 text-2xl tracking-tight">If the address is on file, a link is on its way.</h2>
        <p className="mt-4 text-ink-soft">Open the email; the link is valid for 30 minutes.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-rule bg-cream-card p-8 relative">
      <span className="absolute -top-2.5 left-6 bg-paper px-3 font-mono text-[11px] uppercase tracking-[0.18em] text-terra-deep">
        Step A · Request
      </span>
      <h2 className="text-2xl tracking-tight">Send the link</h2>
      <p className="mt-3 text-sm text-ink-soft">
        Enter the email on your account. We'll send a reset link that's good for 30 minutes.
      </p>
      <form className="mt-7 space-y-5" onSubmit={onSubmit}>
        <div>
          <label htmlFor="reset-email" className="field-label">
            Email
          </label>
          <input
            id="reset-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field-input"
            required
          />
        </div>
        <button className="btn btn-primary w-full" disabled={submitting}>
          {submitting ? "Sending…" : "Email me a link"}
        </button>
      </form>
    </div>
  );
}

function ConfirmReset({ token }: { token: string }) {
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (pw1 !== pw2) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset({ token, new_password: pw1 });
      navigate("/login", { state: { reset: true } });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Couldn't reset password.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rounded-lg border border-rule bg-cream-card p-8 relative">
      <span className="absolute -top-2.5 left-6 bg-paper px-3 font-mono text-[11px] uppercase tracking-[0.18em] text-terra-deep">
        Step B · Set new
      </span>
      <h2 className="text-2xl tracking-tight">Choose a new one</h2>
      <p className="mt-3 text-sm text-ink-soft">
        Enter a new password below to finish.
      </p>
      <form className="mt-7 space-y-5" onSubmit={onSubmit}>
        <div>
          <label htmlFor="pw1" className="field-label">
            New password
          </label>
          <input
            id="pw1"
            type="password"
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            className="field-input"
            minLength={10}
            required
          />
          <small className="field-help">At least 10 characters. No common passwords.</small>
        </div>
        <div>
          <label htmlFor="pw2" className="field-label">
            Confirm new password
          </label>
          <input
            id="pw2"
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            className="field-input"
            required
          />
        </div>
        {error && (
          <p className="rounded border border-plum/30 bg-plum/[0.04] px-3 py-2 text-sm text-plum">
            {error}
          </p>
        )}
        <button className="btn btn-primary w-full" disabled={submitting}>
          {submitting ? "Updating…" : "Update password & sign in"}
        </button>
      </form>
    </div>
  );
}
